/**
 * System check: is every piece of the deployment working right now?
 *
 * For the moment after a deploy or a cutover. One click runs every probe the
 * backend has — database, AI provider, SMTP, lead middleware, worker,
 * Microsoft sign-in, each website's embed tag, widget.js, the certificate and
 * each bot's training data — and nothing it does sends, creates or trains
 * anything, so the button needs no confirmation. The last run is stored, so
 * the next administrator to open the page sees it without re-running.
 *
 * Deployment-wide rather than per bot, so it ignores the bot switcher.
 */

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api/client';
import type { BuildInfo, CheckStatus, SystemCheckRun } from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Spinner,
  StatusBadge,
} from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { formatDateTime } from '../lib/format';

/** Failures first, then warnings: the rows that need someone come to the top. */
const STATUS_ORDER: Record<CheckStatus, number> = { failed: 0, warning: 1, ok: 2 };

export function SystemCheck(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [run, setRun] = useState<SystemCheckRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRun(await api.lastSystemCheck());
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Could not load the last check',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  async function handleRun(): Promise<void> {
    setRunning(true);
    try {
      setRun(await api.runSystemCheck());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The check could not run');
    } finally {
      setRunning(false);
    }
  }

  if (!isAdmin) {
    return (
      <Card>
        <EmptyState
          title="System check is for administrators."
          hint="It names hosts and integrations a viewer does not need to see."
        />
      </Card>
    );
  }

  const rows = run
    ? [...run.results].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status])
    : [];

  return (
    <div>
      <PageHeader
        title="System check"
        description="Read-only probes of every integration. Nothing is sent, created or trained, so it is safe to run at any time."
        actions={
          <Button onClick={() => void handleRun()} disabled={running}>
            {running ? 'Checking…' : 'Run check'}
          </Button>
        }
      />

      <DeploymentCard />

      {error && (
        <div className="mb-4">
          <ErrorNotice message={error} />
        </div>
      )}

      {loading ? (
        <Spinner label="Loading the last check…" />
      ) : !run ? (
        <Card>
          <EmptyState
            title="No check has been run yet."
            hint="Run one now; it takes about fifteen seconds."
          />
        </Card>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            <span>
              <span className="font-medium text-emerald-700">{run.ok} OK</span>
              {' · '}
              <span className="font-medium text-amber-700">
                {run.warnings} {run.warnings === 1 ? 'warning' : 'warnings'}
              </span>
              {' · '}
              <span className="font-medium text-red-700">{run.failed} failed</span>
            </span>
            <span className="text-slate-500">
              Last run {formatDateTime(run.ran_at)} by {run.ran_by || 'unknown'} in{' '}
              {(run.duration_ms / 1000).toFixed(1)} s
            </span>
          </div>

          <Card className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2">Check</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Detail</th>
                  <th className="px-4 py-2 text-right">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.key} className="align-top">
                    <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-800">
                      {row.label}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-600">{row.detail}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-slate-500">
                      {row.duration_ms} ms
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

/**
 * Which GitHub commit is live, from the stamp the deploy workflow writes. The
 * first thing to look at after pushing: if the commit here is not the one just
 * pushed, the Actions run has not finished or has failed.
 */
function DeploymentCard(): JSX.Element {
  const [build, setBuild] = useState<BuildInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .buildInfo()
      .then(setBuild)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : 'Could not load the build'),
      );
  }, []);

  const repoUrl = build?.repository ? `https://github.com/${build.repository}` : null;

  return (
    <Card className="mb-4 p-4">
      <h2 className="mb-2 text-sm font-semibold text-slate-700">
        Deployment (GitHub Actions)
      </h2>
      {error ? (
        <p className="text-sm text-red-700">{error}</p>
      ) : !build ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : !build.commit ? (
        <p className="text-sm text-slate-500">
          No build stamp. This instance was not deployed by the GitHub workflow
          (for example a local or docker build).
        </p>
      ) : (
        <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
          <dt className="text-slate-500">Branch</dt>
          <dd className="font-medium text-slate-800">{build.branch ?? '—'}</dd>
          <dt className="text-slate-500">Commit</dt>
          <dd>
            {repoUrl ? (
              <a
                href={`${repoUrl}/commit/${build.commit}`}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-brand-blue underline"
              >
                {build.commit.slice(0, 7)}
              </a>
            ) : (
              <code>{build.commit.slice(0, 7)}</code>
            )}
          </dd>
          <dt className="text-slate-500">Workflow run</dt>
          <dd>
            {repoUrl && build.run_id ? (
              <a
                href={`${repoUrl}/actions/runs/${build.run_id}`}
                target="_blank"
                rel="noreferrer"
                className="text-brand-blue underline"
              >
                #{build.run_number ?? build.run_id}
              </a>
            ) : (
              '—'
            )}
          </dd>
          <dt className="text-slate-500">Built</dt>
          <dd className="text-slate-800">{formatDateTime(build.built_at)}</dd>
        </dl>
      )}
    </Card>
  );
}
